import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Loader } from 'components/Loader/Loader'
import CreatorProfileHolder from 'pages/creatorVerification/components/CreatorProfileHolder/CreatorProfileHolder'
import { useUserProfile } from 'store'
import screens from 'styles/screens'

import {
    SectionTitle,
    StepHeader,
    StepNumber
} from '../../../../components-ref/templates/verification/VerificationPersonalInformation/VerificationPersonalInformation'

interface IProps {
    hideUpload: boolean
}

export function VerificationUplaodImagesContent({
    hideUpload
}: IProps) {
    const userData = useUserProfile()
    const { t } = useTranslation()

    if (!userData) return <Loader />

    return (
        <Container>
            <StepHeader>
                <StepNumber>
                    {!hideUpload ? 'Step 1' : 'Step 3'}
                </StepNumber>

                <SectionTitle>
                    {!hideUpload
                        ? t('uploadYourImages')
                        : t('verifyYourIdentity')}
                </SectionTitle>
            </StepHeader>

            <ProfileSection>
                {!hideUpload && (
                    <>
                        <CoverTooltip>
                            <TooltipTitle>
                                Add a Cover Photo
                            </TooltipTitle>

                            <TooltipText>
                                Upload a banner image to showcase your style.
                            </TooltipText>

                            <TooltipPointer />
                        </CoverTooltip>

                        <AvatarTooltip>
                            <TooltipTitle>
                                Add a Profile Photo
                            </TooltipTitle>

                            <TooltipText>
                                Upload a profile picture to represent you.
                            </TooltipText>

                            <TooltipPointer />
                        </AvatarTooltip>
                    </>
                )}

                <CreatorProfileHolder
                    hideUpload={hideUpload}
                    id={userData?.id}
                    cover={userData?.cover?.url}
                    avatar={userData.avatar?.url}
                    display_name={userData?.display_name}
                    follower_count={userData?.follower_count}
                    friend_count={userData?.friend_count}
                    username={userData?.username}
                />
            </ProfileSection>

            {!hideUpload && (
                <DesktopSteps>
                    <InstructionCard>
                        <InstructionNumber>
                            1
                        </InstructionNumber>

                        <InstructionContent>
                            <InstructionTitle>
                                Upload a Cover Photo
                            </InstructionTitle>

                            <InstructionText>
                                Click the camera icon on the top left to upload
                                a banner image.
                            </InstructionText>

                            <InstructionHint>
                                Recommended size: 1920 × 600px
                            </InstructionHint>
                        </InstructionContent>
                    </InstructionCard>

                    <InstructionCard>
                        <InstructionNumber>
                            2
                        </InstructionNumber>

                        <InstructionContent>
                            <InstructionTitle>
                                Upload a Profile Photo
                            </InstructionTitle>

                            <InstructionText>
                                Click the camera icon on your profile picture to
                                upload your avatar.
                            </InstructionText>

                            <InstructionHint>
                                Recommended size: 400 × 400px
                            </InstructionHint>
                        </InstructionContent>
                    </InstructionCard>
                </DesktopSteps>
            )}
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
`

const ProfileSection = styled.div`
    position: relative;
    width: 100%;
`

const TooltipBase = styled.div`
    position: absolute;

    z-index: 10;

    width: 20rem;

    padding: 1.3rem 1.5rem;

    border: 0.1rem solid rgba(13, 20, 68, 0.04);
    border-radius: 1.4rem;

    background: rgba(255, 255, 255, 0.96);

    box-shadow:
        0 1.2rem 3rem rgba(17, 29, 41, 0.14);

    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);

    pointer-events: none;

    @media (max-width: ${screens.medium}) {
        width: 15.5rem;

        padding: 1rem 1.1rem;

        border-radius: 1.2rem;
    }
`

const CoverTooltip = styled(TooltipBase)`
    top: 1rem;
    left: 4rem;

    @media (max-width: ${screens.medium}) {
        top: 1rem;
        left: 1rem;
    }
`

const AvatarTooltip = styled(TooltipBase)`
    top: 11rem;
    right: 2rem;

    @media (max-width: ${screens.medium}) {
        top: 12.5rem;
        right: 1rem;
    }
`

const TooltipTitle = styled.div`
    color: #0d1444;

    font-family: Montserrat, sans-serif;
    font-size: 1.25rem;
    font-weight: 650;
    line-height: 1.6rem;

    @media (max-width: ${screens.medium}) {
        font-size: 1.05rem;
        line-height: 1.4rem;
    }
`

const TooltipText = styled.div`
    margin-top: 0.35rem;

    color: #667085;

    font-family: Montserrat, sans-serif;
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.45rem;

    @media (max-width: ${screens.medium}) {
        font-size: 0.95rem;
        line-height: 1.3rem;
    }
`

const TooltipPointer = styled.div`
    position: absolute;

    left: 2rem;
    bottom: -0.7rem;

    width: 1.4rem;
    height: 1.4rem;

    background: rgba(255, 255, 255, 0.96);

    transform: rotate(45deg);

    border-right: 0.1rem solid rgba(13, 20, 68, 0.04);
    border-bottom: 0.1rem solid rgba(13, 20, 68, 0.04);
`

const DesktopSteps = styled.div`
    width: 100%;

    display: flex;
    flex-direction: column;

    gap: 1.4rem;

    margin-top: 3rem;

    @media (max-width: ${screens.medium}) {
        display: none;
    }
`

const InstructionCard = styled.div`
    width: 100%;

    display: flex;
    align-items: center;

    gap: 1.8rem;

    padding: 1.8rem 2rem;

    border-radius: 2rem;

`

const InstructionNumber = styled.div`
    width: 4.6rem;
    height: 4.6rem;

    flex-shrink: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 50%;

    color: #ffffff;

    font-family: Montserrat, sans-serif;
    font-size: 1.5rem;
    font-weight: 650;

    background: #2894ff;

    box-shadow:
        0 0 0 0.6rem rgba(40, 148, 255, 0.08),
        0 0.8rem 2rem rgba(40, 148, 255, 0.15);
`

const InstructionContent = styled.div`
    min-width: 0;

    display: flex;
    flex-direction: column;
`

const InstructionTitle = styled.div`
    color: #0d1444;

    font-family: Montserrat, sans-serif;
    font-size: 1.5rem;
    font-weight: 650;
    line-height: 2rem;
`

const InstructionText = styled.div`
    margin-top: 0.3rem;

    color: #667085;

    font-family: Montserrat, sans-serif;
    font-size: 1.3rem;
    font-weight: 500;
    line-height: 1.8rem;
`

const InstructionHint = styled.div`
    margin-top: 0.35rem;

    color: #9aa3ad;

    font-family: Montserrat, sans-serif;
    font-size: 1.1rem;
    font-weight: 500;
    line-height: 1.5rem;
`